/* Hand-rolled SVG charts — no charting dependency, full control of the look. */

import { useMemo, useState } from 'react';
import { compactMoney, compactNum, num } from '../lib/format.js';

const TONES = {
  amber: '#ff8a1f',
  amber2: '#ffb020',
  hazard: '#ffe14d',
  lime: '#a3e635',
  teal: '#2dd4bf',
  blue: '#5aa9ff',
  violet: '#a78bfa',
  green: '#34d399',
  red: '#fb5f5f',
  muted: '#6d7889',
};

const buildPath = (points, close = false, baseY = 0) => {
  if (!points.length) return '';
  let d = `M ${points[0][0]} ${points[0][1]}`;
  for (let i = 1; i < points.length; i += 1) {
    const [px, py] = points[i - 1];
    const [x, y] = points[i];
    const cx = (px + x) / 2;
    d += ` C ${cx} ${py} ${cx} ${y} ${x} ${y}`;
  }
  if (close) d += ` L ${points[points.length - 1][0]} ${baseY} L ${points[0][0]} ${baseY} Z`;
  return d;
};

/* ------------------------------------------------------------ sparkline */

export function Sparkline({ values = [], tone = 'amber', height = 30, width = 96, fill = true }) {
  const data = values.map((v) => Number(v) || 0);
  if (data.length < 2) return <span className="dimmer" style={{ fontSize: 'var(--fs-xs)' }}>—</span>;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const span = max - min || 1;
  const points = data.map((v, i) => [
    (i / (data.length - 1)) * width,
    height - 3 - ((v - min) / span) * (height - 8),
  ]);
  const color = TONES[tone] || tone;
  const id = `sp-${tone}-${Math.round(max)}-${data.length}`;
  return (
    <svg className="spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.34" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {fill ? <path d={buildPath(points, true, height)} fill={`url(#${id})`} /> : null}
      <path d={buildPath(points)} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <circle cx={points[points.length - 1][0]} cy={points[points.length - 1][1]} r="2.4" fill={color} />
    </svg>
  );
}

/* ------------------------------------------------------------- area chart */

/**
 * Multi-series area/line chart with hover readout.
 * series: [{ key, label, color, values: number[] }]
 * labels: string[] (x axis)
 */
export function AreaChart({ series = [], labels = [], height = 220, valueFormat = (v) => num(v) }) {
  const [hover, setHover] = useState(null);
  const width = 720;
  const pad = { top: 14, right: 12, bottom: 26, left: 52 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;

  const { max, ticks, xAt, yAt } = useMemo(() => {
    const all = series.flatMap((s) => s.values.map((v) => Number(v) || 0));
    const rawMax = Math.max(1, ...all);
    const step = Math.pow(10, Math.floor(Math.log10(rawMax))) / 2;
    const top = Math.ceil(rawMax / step) * step;
    const count = labels.length || 1;
    const tickList = [0, top / 4, top / 2, (top * 3) / 4, top];
    return {
      max: top,
      ticks: tickList,
      xAt: (i) => pad.left + (count <= 1 ? innerW / 2 : (i / (count - 1)) * innerW),
      yAt: (v) => pad.top + innerH - (Number(v) / top) * innerH,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, labels, height]);

  if (!series.length || !labels.length) {
    return <div className="empty" style={{ padding: 30 }}><span className="dim">Not enough data yet</span></div>;
  }

  const onMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * width;
    const ratio = (x - pad.left) / innerW;
    const index = Math.max(0, Math.min(labels.length - 1, Math.round(ratio * (labels.length - 1))));
    setHover(index);
  };

  return (
    <div className="chart-wrap">
      <svg
        className="chart-svg"
        viewBox={`0 0 ${width} ${height}`}
        style={{ height }}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          {series.map((s) => (
            <linearGradient key={s.key} id={`ar-${s.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color || TONES.amber} stopOpacity="0.32" />
              <stop offset="100%" stopColor={s.color || TONES.amber} stopOpacity="0.02" />
            </linearGradient>
          ))}
        </defs>

        <g className="chart-grid">
          {ticks.map((t, i) => (
            <g key={i}>
              <line x1={pad.left} x2={width - pad.right} y1={yAt(t)} y2={yAt(t)} />
              <text className="chart-axis" x={pad.left - 8} y={yAt(t) + 3} textAnchor="end">
                {compactMoney(t)}
              </text>
            </g>
          ))}
        </g>

        {series.map((s) => {
          const points = s.values.map((v, i) => [xAt(i), yAt(v)]);
          return (
            <g key={s.key}>
              <path d={buildPath(points, true, pad.top + innerH)} fill={`url(#ar-${s.key})`} />
              <path d={buildPath(points)} fill="none" stroke={s.color || TONES.amber} strokeWidth="2" strokeLinecap="round" />
            </g>
          );
        })}

        {labels.map((label, i) => {
          const show = labels.length <= 12 || i % Math.ceil(labels.length / 8) === 0;
          if (!show) return null;
          return (
            <text key={label + i} className="chart-axis" x={xAt(i)} y={height - 8} textAnchor="middle">
              {label}
            </text>
          );
        })}

        {hover !== null ? (
          <g>
            <line x1={xAt(hover)} x2={xAt(hover)} y1={pad.top} y2={pad.top + innerH} stroke="rgba(255,255,255,.28)" strokeDasharray="3 3" />
            {series.map((s) => (
              <circle key={s.key} cx={xAt(hover)} cy={yAt(s.values[hover])} r="4" fill={s.color} stroke="#0e1116" strokeWidth="2" />
            ))}
          </g>
        ) : null}
      </svg>

      {hover !== null ? (
        <div
          className="card flat"
          style={{
            position: 'absolute',
            top: 6,
            right: 6,
            padding: '9px 11px',
            minWidth: 148,
            pointerEvents: 'none',
          }}
        >
          <div className="label mb-4">{labels[hover]}</div>
          {series.map((s) => (
            <div key={s.key} className="row between gap-12" style={{ fontSize: 'var(--fs-sm)' }}>
              <span className="muted row gap-6">
                <i style={{ width: 8, height: 8, borderRadius: 2, background: s.color, display: 'inline-block' }} />
                {s.label}
              </span>
              <span className="num">{valueFormat(s.values[hover])}</span>
            </div>
          ))}
        </div>
      ) : null}

      <div className="legend mt-8">
        {series.map((s) => (
          <span key={s.key}>
            <i style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ bar chart */

export function Bars({ data = [], height = 170, tone = 'amber', format = (v) => compactNum(v) }) {
  const max = Math.max(1, ...data.map((d) => Number(d.value) || 0));
  const [hover, setHover] = useState(null);
  const color = TONES[tone] || tone;
  if (!data.length) return <div className="empty" style={{ padding: 24 }}><span className="dim">No data</span></div>;
  return (
    <div>
      <div className="row" style={{ alignItems: 'flex-end', gap: 4, height }}>
        {data.map((d, i) => {
          const ratio = (Number(d.value) || 0) / max;
          return (
            <div
              key={d.label ?? i}
              className="grow"
              style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%', gap: 4, minWidth: 0 }}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              title={`${d.label}: ${format(d.value)}`}
            >
              <div
                style={{
                  height: `${Math.max(2, ratio * 100)}%`,
                  borderRadius: '4px 4px 2px 2px',
                  background: hover === i
                    ? `linear-gradient(180deg, ${color}, ${color}aa)`
                    : `linear-gradient(180deg, ${color}cc, ${color}44)`,
                  transition: 'all 160ms var(--ease)',
                  boxShadow: hover === i ? `0 0 14px -2px ${color}` : 'none',
                }}
              />
            </div>
          );
        })}
      </div>
      <div className="row between dim mt-8" style={{ fontSize: 'var(--fs-xs)' }}>
        <span>{data[0]?.label}</span>
        {hover !== null ? <span className="amber num">{data[hover].label}: {format(data[hover].value)}</span> : null}
        <span>{data[data.length - 1]?.label}</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------- horizontal bars */

export function HBarList({ rows = [], format = (v) => num(v), tone = 'amber', labelWidth = 150 }) {
  const max = Math.max(1, ...rows.map((r) => Number(r.value) || 0));
  if (!rows.length) return <div className="empty" style={{ padding: 24 }}><span className="dim">No data</span></div>;
  return (
    <div className="col gap-12">
      {rows.map((row, i) => (
        <div
          key={row.label ?? i}
          className="bar-row"
          style={{ gridTemplateColumns: `${labelWidth}px 1fr auto` }}
        >
          <span className="truncate" style={{ fontSize: 'var(--fs-sm)' }} title={row.label}>{row.label}</span>
          <div className="bar-track">
            <div
              className="bar-fill"
              style={{
                width: `${Math.max(1.5, ((Number(row.value) || 0) / max) * 100)}%`,
                '--from': TONES[row.tone || tone],
                '--to': TONES[row.tone2 || `${tone}2`] || TONES[tone],
              }}
            />
          </div>
          <span className="num" style={{ fontSize: 'var(--fs-sm)', color: 'var(--fg-2)' }}>{format(row.value, row)}</span>
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------- donut */

export function Donut({ value = 0, max = 100, label, sublabel, tone = 'amber', size = 132, thickness = 12 }) {
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  const ratio = Math.max(0, Math.min(1, Number(value) / (Number(max) || 1)));
  const color = TONES[tone] || tone;
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(255,255,255,.08)" strokeWidth={thickness} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={`${circumference * ratio} ${circumference}`}
          style={{ transition: 'stroke-dasharray 600ms var(--ease)' }}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center' }}>
        <div>
          <div className="num" style={{ fontSize: 'var(--fs-2xl)', fontWeight: 660, letterSpacing: '-0.03em' }}>{label}</div>
          {sublabel ? <div className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{sublabel}</div> : null}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- heat strip */

export function HeatStrip({ cells = [], height = 22, tone = 'amber' }) {
  const max = Math.max(1, ...cells.map((c) => Number(c.value) || 0));
  const color = TONES[tone] || tone;
  return (
    <div className="heat">
      {cells.map((cell, i) => {
        const ratio = (Number(cell.value) || 0) / max;
        return (
          <div
            key={cell.label ?? i}
            className="heat-cell"
            style={{ height, background: ratio ? `${color}${Math.round(20 + ratio * 80).toString(16).padStart(2, '0')}` : undefined }}
            title={`${cell.label}: ${num(cell.value)}`}
          />
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------ coverage map */

/**
 * Stylized coverage map: projects real lat/lng into a dark "ops board" frame.
 * No tiles, no network — it reads as a dispatch screen, not a Google Map.
 */
export function CoverageMap({ points = [], height = 380, onSelect, highlightId }) {
  const width = 760;
  const valid = points.filter((p) => p.lat && p.lng);
  if (!valid.length) {
    return (
      <div className="map-frame" style={{ height, display: 'grid', placeItems: 'center' }}>
        <span className="dim">No geotagged activity yet</span>
      </div>
    );
  }
  const lats = valid.map((p) => p.lat);
  const lngs = valid.map((p) => p.lng);
  const pad = 6;
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const latSpan = maxLat - minLat || 0.05;
  const lngSpan = maxLng - minLng || 0.05;

  const project = (lat, lng) => [
    pad + ((lng - minLng) / lngSpan) * (100 - pad * 2),
    100 - (pad + ((lat - minLat) / latSpan) * (100 - pad * 2)),
  ];

  const statusTone = {
    posted: TONES.amber,
    assigned: TONES.blue,
    en_route: TONES.violet,
    checked_in: TONES.violet,
    in_progress: TONES.lime,
  };

  return (
    <div className="map-frame" style={{ height }}>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ width: '100%', height: '100%' }}>
        <defs>
          <pattern id="mgrid" width="6" height="6" patternUnits="userSpaceOnUse">
            <path d="M 6 0 L 0 0 0 6" fill="none" stroke="rgba(255,255,255,.045)" strokeWidth="0.16" />
          </pattern>
        </defs>
        <rect width="100" height="100" fill="url(#mgrid)" />
        <path className="map-water" d="M 82 0 L 100 0 L 100 100 L 74 100 C 84 78 74 58 86 40 C 92 28 88 12 82 0 Z" />
        <g className="map-road">
          <path d="M 0 62 C 22 58 34 40 58 36 C 74 33 84 26 100 22" strokeDasharray="1.2 1.4" strokeWidth="0.6" />
          <path d="M 6 100 C 14 78 30 66 48 56 C 66 46 82 34 96 34" strokeDasharray="1.2 1.4" strokeWidth="0.5" />
          <path d="M 30 0 C 34 18 30 34 20 100" strokeDasharray="0.8 1.6" strokeWidth="0.45" />
        </g>
        {valid.map((p) => {
          const [x, y] = project(p.lat, p.lng);
          const color = statusTone[p.status] || TONES.muted;
          const active = p.status === 'in_progress';
          const isHot = highlightId === p.id;
          return (
            <g
              key={p.id ?? `${p.lat}-${p.lng}`}
              className="map-pin"
              transform={`translate(${x} ${y})`}
              onClick={() => onSelect?.(p)}
            >
              {active || isHot ? <circle className="halo" r="2.4" fill={color} opacity="0.35" /> : null}
              <circle r={isHot ? 1.5 : 1.05} fill={color} stroke="#0b0f14" strokeWidth="0.35" />
              <circle r="3" fill="transparent" />
            </g>
          );
        })}
      </svg>
      <div
        className="row gap-12 wrap"
        style={{ position: 'absolute', left: 12, bottom: 10, fontSize: 'var(--fs-xs)', color: 'var(--fg-2)' }}
      >
        {[
          ['Posted', TONES.amber],
          ['Assigned', TONES.blue],
          ['En route / checked in', TONES.violet],
          ['In progress', TONES.lime],
        ].map(([label, color]) => (
          <span key={label} className="row gap-6">
            <i style={{ width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block' }} />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Weekday × hour heat grid for dock activity. */
export function WeekHeat({ data = [] }) {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const lookup = new Map(data.map((d) => [`${d.weekday}-${d.hour}`, Number(d.value) || 0]));
  const max = Math.max(1, ...[...lookup.values()]);
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: `34px repeat(24, 1fr)`, gap: 2, minWidth: 620 }}>
        <span />
        {hours.map((h) => (
          <span key={h} className="dimmer center" style={{ fontSize: 9 }}>
            {h % 3 === 0 ? h : ''}
          </span>
        ))}
        {days.map((day, di) => (
          <FragmentRow key={day} day={day} di={di} hours={hours} lookup={lookup} max={max} />
        ))}
      </div>
    </div>
  );
}

function FragmentRow({ day, di, hours, lookup, max }) {
  return (
    <>
      <span className="dim" style={{ fontSize: 10, alignSelf: 'center' }}>{day}</span>
      {hours.map((h) => {
        const value = lookup.get(`${di}-${h}`) || 0;
        const ratio = value / max;
        return (
          <span
            key={h}
            title={`${day} ${h}:00 — ${value} jobs`}
            style={{
              height: 15,
              borderRadius: 3,
              background: ratio
                ? `rgba(255, 138, 31, ${0.12 + ratio * 0.78})`
                : 'rgba(255,255,255,.04)',
            }}
          />
        );
      })}
    </>
  );
}
